import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import useNotification from './useNotification';
import {
  getPermissions,
  getRolePermissions,
  assignPermissionsToRole,
  Permission
} from '../services/permissionService';

export interface PermissionsByCategory {
  [category: string]: Permission[];
}

export interface UsePermissionsOptions {
  roleId?: number;
}

export function usePermissions(options?: UsePermissionsOptions) {
  const notification = useNotification();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [permissionsByCategory, setPermissionsByCategory] = useState<PermissionsByCategory>({});
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
  
  // Agregar referencia para prevenir bucles infinitos
  const isDataFetching = useRef(false);
  const fetchAttempts = useRef(0);
  const maxFetchAttempts = 3;
  const lastSavedPermissions = useRef<number[]>([]);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Obtener permisos del usuario desde Redux
  const { permissions: userPermissions, isSuperAdmin } = useSelector((state: RootState) => state.auth);

  // Funciones para verificar permisos
  const hasPermission = useCallback((permissionCode: string): boolean => {
    // Si es superadmin, tiene todos los permisos
    if (isSuperAdmin) return true;
    
    // Verificar si el usuario tiene el permiso específico
    return userPermissions.includes(permissionCode);
  }, [userPermissions, isSuperAdmin]);

  const hasAnyPermission = useCallback((permissionCodes: string[]): boolean => {
    // Si es superadmin, tiene todos los permisos
    if (isSuperAdmin) return true;
    
    // Verificar si el usuario tiene al menos uno de los permisos
    return permissionCodes.some(code => userPermissions.includes(code));
  }, [userPermissions, isSuperAdmin]);

  const hasAllPermissions = useCallback((permissionCodes: string[]): boolean => {
    // Si es superadmin, tiene todos los permisos
    if (isSuperAdmin) return true;
    
    // Verificar si el usuario tiene todos los permisos especificados
    return permissionCodes.every(code => userPermissions.includes(code));
  }, [userPermissions, isSuperAdmin]);

  // Definimos fetchData como función estable que lee roleId de la ref para evitar dependencias cambiantes
  const optionsRef = useRef(options);
  
  // Actualizar la referencia cuando cambien las opciones
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);
  
  const fetchData = useCallback(async () => {
    // Usar la referencia para acceder al roleId actual
    const currentOptions = optionsRef.current;
    
    // Si no hay roleId, no hay necesidad de cargar los permisos específicos
    if (!currentOptions?.roleId) {
      setLoading(false);
      return;
    }
    
    // Evitar llamadas concurrentes y limitar intentos
    if (isDataFetching.current) {
      return;
    }
    
    // Si ya se intentó demasiadas veces, mostrar error y detener
    if (fetchAttempts.current >= maxFetchAttempts) {
      setError('Se excedió el número máximo de intentos de carga. Por favor, cierre y vuelva a abrir esta ventana.');
      setLoading(false);
      return;
    }
    
    fetchAttempts.current += 1;

    try {
      isDataFetching.current = true;
      setLoading(true);
      setError(null);

      // Añadir un pequeño retraso para evitar múltiples llamadas rápidas
      await new Promise(resolve => setTimeout(resolve, 100));

      // Obtener todos los permisos y los permisos asignados al rol
      const currentRoleId = currentOptions?.roleId;
      if (!currentRoleId) {
        throw new Error('ID de rol no disponible');
      }
      
      const [allPermissions, rolePermissions] = await Promise.all([
        getPermissions(),
        getRolePermissions(currentRoleId),
      ]);
      
      // Verificar que los datos son válidos
      if (!Array.isArray(allPermissions) || !Array.isArray(rolePermissions)) {
        console.error('Datos inválidos:', { allPermissions, rolePermissions });
        throw new Error('Formato de datos inválido recibido del servidor');
      }

      // Organizar permisos por categoría
      const permissionsByCat: PermissionsByCategory = {};
      allPermissions.forEach((permission) => {
        if (!permission || !permission.category) return; // Evitar permisos inválidos
        
        if (!permissionsByCat[permission.category]) {
          permissionsByCat[permission.category] = [];
        }
        permissionsByCat[permission.category].push(permission);
      });

      // Asegurar que hay categorías válidas
      if (Object.keys(permissionsByCat).length === 0) {
        setPermissionsByCategory({});
        setPermissions([]);
        setSelectedPermissions([]);
        setError('No se encontraron permisos para mostrar');
      } else {
        setPermissions(allPermissions);
        setPermissionsByCategory(permissionsByCat);
        setSelectedPermissions(rolePermissions);
      }
      
      // Actualizar referencia de los últimos permisos guardados
      lastSavedPermissions.current = [...rolePermissions];
      
      // Reiniciar contador de intentos tras éxito
      fetchAttempts.current = 0;
    } catch (err) {
      console.error('Error detallado al cargar permisos:', err);
      
      let errorMessage = 'Error al cargar los permisos. Por favor, intente nuevamente.';
      
      // Proporcionar mensajes de error más específicos
      if (err instanceof Error) {
        if (err.message.includes('Network Error') || err.message.includes('fetch')) {
          errorMessage = 'Error de conexión. Verifique su conexión a internet.';
        } else if (err.message.includes('401') || err.message.includes('403')) {
          errorMessage = 'No tiene permisos para acceder a esta información.';
        } else if (err.message.includes('404')) {
          errorMessage = 'El rol especificado no existe.';
        } else if (err.message.includes('500')) {
          errorMessage = 'Error interno del servidor. Contacte al administrador.';
        }
      }
      
      setError(errorMessage);
      // Usar notification directamente sin dependencia
      notification.showError(errorMessage);
      
      // Si hay error, limpiar los datos
      setPermissions([]);
      setPermissionsByCategory({});
      setSelectedPermissions([]);
    } finally {
      setLoading(false);
      isDataFetching.current = false;
      
      // Programar limpieza de bandera tras un tiempo
      setTimeout(() => {
        isDataFetching.current = false;
      }, 500);
    }
  }, []); // Sin dependencias para evitar recreaciones

  // Cargar permisos y permisos asignados al rol al montar el componente
  useEffect(() => {
    // Si no hay roleId, no es necesario cargar datos
    if (!options?.roleId) {
      setLoading(false);
      return;
    }
    
    // Limpiar estado al cambiar de rol
    setPermissions([]);
    setPermissionsByCategory({});
    setSelectedPermissions([]);
    setError(null);
    
    // Reiniciar contador de intentos
    fetchAttempts.current = 0;
    
    // Cargar datos
    fetchData();
    
    // Limpieza al desmontar
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [options?.roleId]);

  // Manejar cambio en un permiso
  const handlePermissionChange = (permissionId: number) => {
    setSelectedPermissions((prev) => {
      if (prev.includes(permissionId)) {
        return prev.filter((id) => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  // Seleccionar/deseleccionar todos los permisos de una categoría
  const handleCategoryChange = (category: string, checked: boolean) => {
    const categoryPermissionIds = permissionsByCategory[category]?.map((p) => p.id) || [];

    setSelectedPermissions((prev) => {
      if (checked) {
        // Añadir permisos de esta categoría que no estén ya seleccionados
        const newPermissions = categoryPermissionIds.filter((id) => !prev.includes(id));
        return [...prev, ...newPermissions];
      } else {
        // Quitar todos los permisos de esta categoría
        return prev.filter((id) => !categoryPermissionIds.includes(id));
      }
    });
  };

  // Verificar si todos los permisos de una categoría están seleccionados
  const isCategoryChecked = (category: string) => {
    const categoryPermissionIds = permissionsByCategory[category]?.map((p) => p.id) || [];
    return categoryPermissionIds.length > 0 && 
      categoryPermissionIds.every((id) => selectedPermissions.includes(id));
  };

  // Verificar si algunos permisos de una categoría están seleccionados
  const isCategoryIndeterminate = (category: string) => {
    const categoryPermissionIds = permissionsByCategory[category]?.map((p) => p.id) || [];
    const checkedCount = categoryPermissionIds.filter((id) => selectedPermissions.includes(id)).length;
    return checkedCount > 0 && checkedCount < categoryPermissionIds.length;
  };

  // Guardar cambios
  const savePermissions = async () => {
    const currentOptions = optionsRef.current;
    if (!currentOptions?.roleId) {
      notification.showError('No se especificó un ID de rol');
      return false;
    }
    
    // Evitar guardar si no hay cambios
    const currentPermissions = [...selectedPermissions].sort();
    const prevPermissions = [...lastSavedPermissions.current].sort();
    
    if (JSON.stringify(currentPermissions) === JSON.stringify(prevPermissions)) {
      notification.showInfo('No hay cambios para guardar');
      return true;
    }
    
    // Evitar múltiples guardados simultáneos
    if (saving) {
      return false;
    }

    // Cancelar cualquier temporizador anterior
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      await assignPermissionsToRole(currentOptions.roleId, selectedPermissions);
      setSuccessMessage('Permisos actualizados correctamente');
      notification.showSuccess('Permisos actualizados correctamente');
      
      // Actualizar la referencia a los últimos permisos guardados
      lastSavedPermissions.current = [...selectedPermissions];
      
      return true;
    } catch (err) {
      const errorMessage = 'Error al guardar los permisos. Por favor, intente nuevamente.';
      setError(errorMessage);
      notification.showError(errorMessage);
      console.error('Error saving permissions:', err);
      return false;
    } finally {
      setSaving(false);
      
      // Establecer un temporizador para permitir otra operación de guardado
      // solo después de un tiempo para evitar llamadas rápidas repetidas
      saveTimeoutRef.current = setTimeout(() => {
        saveTimeoutRef.current = null;
      }, 1000);
    }
  };

  // Recargar permisos
  const refresh = () => {
    if (!isDataFetching.current && fetchAttempts.current < maxFetchAttempts) {
      fetchData();
    }
  };

  return {
    loading,
    saving,
    error,
    successMessage,
    permissions,
    permissionsByCategory,
    selectedPermissions,
    setSelectedPermissions,
    handlePermissionChange,
    handleCategoryChange,
    isCategoryChecked,
    isCategoryIndeterminate,
    savePermissions,
    refresh,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions
  };
}

export default usePermissions; 